/**
 * "Open" links for timeline entries (Moments): the original email in Gmail or Apple Mail, or the event's own URL.
 * Pure: the caller decides how to open the link (desktop shell vs. a plain anchor).
 */
import type { TimelineEntry } from "@hub/shared";

export interface MomentLink {
  href: string;
  label: string;
  kind: "gmail" | "apple_mail" | "event";
}

/** Gmail message ids are hex strings (16 or so characters); Apple Mail ids look like RFC 5322 Message-IDs with an @. */
export function looksLikeGmailId(providerId: string): boolean {
  return /^[0-9a-f]{12,24}$/i.test(providerId.trim());
}

export function gmailUrl(providerId: string): string {
  return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(providerId.trim())}`;
}

/** Apple Mail opens `message://<Message-ID>`; the angle brackets are part of the URL and must be percent-encoded. */
export function appleMailUrl(providerId: string): string {
  const id = providerId.trim().replace(/^<|>$/g, "");
  return `message://%3C${encodeURIComponent(id)}%3E`;
}

export function momentLink(entry: TimelineEntry): MomentLink | null {
  if (entry.email) {
    const id = entry.email.providerId;
    if (!id || !id.trim()) return null;
    return looksLikeGmailId(id) ? { href: gmailUrl(id), label: "Open in Gmail", kind: "gmail" } : { href: appleMailUrl(id), label: "Open in Mail", kind: "apple_mail" };
  }
  if (entry.event) {
    const url = entry.event.url;
    if (!url || !/^https?:\/\//i.test(url.trim())) return null;
    return { href: url.trim(), label: "Open event", kind: "event" };
  }
  return null;
}
